import type { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { usePostProfileEmailAddresses, useDeleteProfileEmailAddressesEmailid } from '@dhl/receiver-profile-api'
import {
  useSnackbar,
  Alert,
  Subtitle2,
  Box,
  Stack,
  ListItem,
  ListItemText,
  Card,
  Paper,
  List,
} from '@dhl-ui/components'

import { dataLayerPublish } from '@dhl/datalayer'
import { MailboxIllustration } from '@dhl-ui/illustrations'
import { useGetProfile, useIsProfilePrimaryEmailAnonymous } from '~/profile/model/profile/hooks.js'
import {
  EmailAddressForm,
  createValidationSchema,
  type FormValues,
  initialValues,
} from '~/profile/view/settings/emailAdresses/EmailAddressForm.js'
import { StepDescription } from '~/setup/view/components/StepDescription.js'
import { StepForm } from '~/setup/view/components/StepForm.js'
import { useSetTitle, useWizardContext } from '~/setup/model/context.js'
import { steps } from '~/setup/model/steps.js'

export const AdditionalEmailAddresses: FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { onStepComplete, currentStep, setStepSubmittedValues, getStepSubmittedValues } = useWizardContext()

  useSetTitle(t('portal.profileSetup.additionalEmailAddresses.pageTitle'))

  const { data: profile } = useGetProfile()
  const { data: isPrimaryEmailAnonymous } = useIsProfilePrimaryEmailAnonymous()
  const { mutateAsync: addProfileEmailAddress } = usePostProfileEmailAddresses()
  const { mutateAsync: deleteProfileEmailAddress } = useDeleteProfileEmailAddressesEmailid()

  const validationSchema = createValidationSchema(t)
  const previouslySubmittedValues = getStepSubmittedValues<FormValues>(steps.additionalEmailAddress)

  const handleSubmit = async (values: FormValues) => {
    try {
      const { isPreviouslyCompleted } = currentStep

      if (isPreviouslyCompleted && previouslySubmittedValues) {
        const hasChanged = values.email !== previouslySubmittedValues.email

        if (!hasChanged) {
          onStepComplete()
          return
        }

        const existingEmail = profile?.emailAddresses?.find((e) => e.email === previouslySubmittedValues.email)

        if (existingEmail) {
          await deleteProfileEmailAddress({ emailId: existingEmail.emailId })
        }
      }

      await addProfileEmailAddress({
        data: values,
        params: {
          'x-lang': language,
        },
      })

      setStepSubmittedValues(steps.additionalEmailAddress, values)
      onStepComplete()

      enqueueSnackbar(
        <Trans
          i18nKey="portal.profileSetup.additionalEmailAddresses.emailVerificationNotification"
          values={{
            emailAddress: values.email,
          }}
        />,
        { severity: 'info' }
      )
    } catch (error: unknown) {
      const isAlreadyInUseError =
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response.data.key === 'EmailAddressAlreadyInUse'

      const errorText = isAlreadyInUseError
        ? 'portal.profileSetup.additionalEmailAddresses.alreadyExistsError'
        : 'common.pleaseTryAgainErrorMessage'

      if (!isAlreadyInUseError) {
        dataLayerPublish('ERROR', { error, page: 'additional-email-addresses-wizard' })
      }

      enqueueSnackbar(t(errorText), { severity: 'error' })

      throw error
    }
  }

  return (
    <Stack spacing={2}>
      {profile && (
        <StepDescription
          slotProps={{ illustration: { variant: MailboxIllustration } }}
          title={t('portal.profileSetup.additionalEmailAddresses.stepTitle')}
          description={t('portal.profileSetup.additionalEmailAddresses.stepDescription')}
        />
      )}

      <Paper>
        {isPrimaryEmailAnonymous && (
          <Alert data-testid="profile-setup-anonymous-primary-email-message" severity="info" sx={{ m: 2, mb: 0 }}>
            {t('portal.profileSetup.additionalEmailAddresses.anonymousPrimaryEmailMessage')}
          </Alert>
        )}

        <List>
          {profile && (
            <ListItem hasDivider={false}>
              <ListItemText emphasis data-testid="setup-wizard-primary-emailaddress" primary={profile.email}>
                <Subtitle2>{t('portal.profileSetup.additionalEmailAddresses.loginEmailAddress')}</Subtitle2>
              </ListItemText>
            </ListItem>
          )}
        </List>
      </Paper>

      <Box>
        <StepForm
          initialValues={previouslySubmittedValues ?? initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Card>
            <Card.Header title={t('portal.profileSetup.additionalEmailAddresses.pageTitle')} />
            <Card.Content>
              <EmailAddressForm autoFocusInput={false} />
            </Card.Content>
          </Card>
        </StepForm>
      </Box>
    </Stack>
  )
}
